import { useCallback, useEffect, useRef } from 'react';

import { useStatefulObservableEffect } from '@atlassian/rx-hooks';

import { type AIAgentStreamingRequest } from '../api/AssistantService/AIAgentStreamingModels';
import { aiAgentStreamingEffect } from '../effects/aiAgentStreamingEffect';
import { type UserAIAgentStreamingConfig } from '../models/AIAgentStreamingConfig';
import {
	type AIAgentStreamingState,
	getDefaultAIAgentStreamingState,
} from '../models/AIAgentStreamingState';

/**
 * @deprecated {@link https://hello.atlassian.net/browse/ENGHEALTH-29941 Internal documentation for deprecation (no external access)}. Please use `useConvoAIStreaming` instead.
 * more info: https://hello.atlassian.net/wiki/spaces/CONVAI/pages/4886462722/Sunsetting+assistance-service+timeline
 */
export function useAIAgentStreaming<
	TAIAgentStreamingRequest extends AIAgentStreamingRequest,
	TAIAgentContentResponse,
>(
	request: TAIAgentStreamingRequest,
	config: UserAIAgentStreamingConfig,
	onStateChange?: (state: AIAgentStreamingState<TAIAgentContentResponse>) => void,
): [AIAgentStreamingState<TAIAgentContentResponse>, () => void] {
	const refetchId = useRef<string>(crypto.randomUUID());

	const [state, setState] = useStatefulObservableEffect<
		AIAgentStreamingState<TAIAgentContentResponse>,
		[TAIAgentStreamingRequest, UserAIAgentStreamingConfig, string]
	>(aiAgentStreamingEffect, getDefaultAIAgentStreamingState(), [
		request,
		config,
		refetchId.current,
	]);

	useEffect(() => {
		onStateChange?.(state);
	}, [state, onStateChange]);

	const refetch = useCallback(() => {
		refetchId.current = crypto.randomUUID();
		setState({
			inputs: [request, config, refetchId.current],
			state: getDefaultAIAgentStreamingState(),
		});
	}, [request, config, setState]);

	return [state, refetch];
}
